import { monthEndISO, prettyDate } from "./ReconMonthDialog";
import MonthPicker from "./MonthPicker";
import StatementFiles from "./StatementFiles";
import useElapsed from "./useElapsed";

// "YYYY-MM" one month before `ym`.
export function previousMonth(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

interface Props {
  insurerName: string;
  insurerPicker: React.ReactNode;
  previousMonth: string;
  currentMonth: string;
  onPreviousMonth: (ym: string) => void;
  onCurrentMonth: (ym: string) => void;
  // The current period (the sidebar's From/To); To may sit mid-month.
  periodFrom: string;
  periodTo: string;
  previousFiles: File[];
  currentFiles: File[];
  onPreviousFiles: (f: File[]) => void;
  onCurrentFiles: (f: File[]) => void;
  busy: boolean;
  error: string;
  onRun: () => void;
  onSkip: () => void;
}

function Need({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return <li className={ok ? "rf-need rf-need--ok" : "rf-need"}><span aria-hidden="true">{ok ? "✓" : "○"}</span>{children}</li>;
}

// Step 1 of an insurer whose layout opts in: the two months and the two statements.
export default function CustomerVarianceStep(p: Props) {
  const ordered = !!p.previousMonth && !!p.currentMonth && p.previousMonth < p.currentMonth;
  const ready = !!p.insurerName && p.previousFiles.length > 0 && p.currentFiles.length > 0
    && ordered && !!p.periodTo;
  const cut = p.previousMonth ? prettyDate(monthEndISO(p.previousMonth)) : "";
  const monthsDone = ordered && !!p.periodTo;
  const filesDone = p.previousFiles.length > 0 && p.currentFiles.length > 0;
  const elapsed = useElapsed(p.busy);
  return (
    <section className="rf-step">
      <header className="rf-hero">
        <div>
          <h2 className="rf-hero-title">Customer variance check</h2>
          <p className="rf-hero-sub">
            Before reconciling, confirm the insurer has not rewritten history: each customer's
            closing in the previous statement is compared with the balance the current
            statement shows at that same date.
          </p>
        </div>
        <div className="rf-hero-insurer">
          <span className="rf-label">Insurer</span>
          {p.insurerPicker}
        </div>
      </header>

      {/* The main upload page's numbered cards (UploadPage.tsx). */}
      <div className={`card upload-card upload-card--${monthsDone ? "done" : "active"}`}>
        <h3 className="card-title">
          <span className="card-step" aria-hidden="true">{monthsDone ? "✓" : 1}</span>
          Months
        </h3>
        <p className="muted" style={{ margin: "0 0 12px" }}>The previous month is compared at its month end</p>
        <div className="rf-grid2">
          <div>
            <label className="rf-label" htmlFor="rf-prev-month">Previous month</label>
            <MonthPicker id="rf-prev-month" value={p.previousMonth} onChange={p.onPreviousMonth}
              disabled={(ym) => !!p.currentMonth && ym >= p.currentMonth} />
            <p className="rf-caption">Compared at {cut || "—"}</p>
          </div>
          <div>
            <label className="rf-label" htmlFor="rf-curr-month">Current month</label>
            <MonthPicker id="rf-curr-month" value={p.currentMonth} onChange={p.onCurrentMonth} />
            <p className="rf-caption">
              {prettyDate(p.periodFrom) || "—"} to {prettyDate(p.periodTo) || "—"} · sets the
              sidebar period · later rows are listed, not counted
            </p>
          </div>
        </div>
        {p.previousMonth && p.currentMonth && !ordered && (
          <p className="rf-caption rf-caption--warn">The previous month must be earlier than the current month.</p>
        )}
      </div>

      <div className={`card upload-card upload-card--${filesDone ? "done" : monthsDone ? "active" : "todo"}`}>
        <h3 className="card-title">
          <span className="card-step" aria-hidden="true">{filesDone ? "✓" : 2}</span>
          Statements
        </h3>
        <p className="muted" style={{ margin: "0 0 12px" }}>Each month's statement from the insurance company</p>
        <div className="rf-grid2">
          <StatementFiles title="Previous month statement" insurerName={p.insurerName}
            hint="The workbook as received for the previous month, plus any partner statements."
            files={p.previousFiles} onChange={p.onPreviousFiles} />
          <StatementFiles title="Current month statement" insurerName={p.insurerName}
            hint="Carries over to the reconciliation, so it is uploaded once."
            files={p.currentFiles} onChange={p.onCurrentFiles} />
        </div>
      </div>

      {p.error && <p className="rf-error" role="alert">{p.error}</p>}

      <div className="rf-actions">
        <div className="rf-action rf-action--accent">
          <div className="rf-action-title">Customer variance check</div>
          <ul className="rf-needs">
            <Need ok={p.previousFiles.length > 0}>Previous statement</Need>
            <Need ok={p.currentFiles.length > 0}>Current statement</Need>
            <Need ok={ordered}>Months in order</Need>
          </ul>
          <button className="btn btn-primary rf-action-btn" disabled={!ready || p.busy} onClick={p.onRun}>
            {p.busy ? "Checking…" : "Check variances"}
          </button>
          {p.busy && (
            <>
              <div className="progress-indeterminate" />
              <p className="muted" style={{ margin: 0 }} aria-live="polite">
                A large statement can take a minute or more. The results open automatically
                when it's done.{elapsed > 0 && ` · ${elapsed}s elapsed`}
              </p>
            </>
          )}
        </div>
        <div className="rf-action">
          <div className="rf-action-title">Reconciliation</div>
          <p className="rf-action-text">
            Skip the check and reconcile the current statement against the CRI ledger. Any
            current statement uploaded here carries over.
          </p>
          <button type="button" className="btn rf-action-btn" onClick={p.onSkip}>
            Go to reconciliation →
          </button>
        </div>
      </div>
    </section>
  );
}
